import {
  Avatar,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  CardProps,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import { FC } from 'react'
import { IconButton, Icons } from 'utoniq-ui'

import { CommentBoxFragment } from '@/clients/utoniq-core/schema'
import { useRouter } from '@/core'

export type CommentBoxProps = {
  comment: CommentBoxFragment
  fullWidth?: boolean
  sx?: CardProps['sx']
  likeComment?: () => void
}

export const CommentBox: FC<CommentBoxProps> = ({
  comment: c,
  fullWidth,
  sx,
  likeComment,
}) => {
  const { routerPages, generatePath } = useRouter()

  return (
    <Card
      key={c.id}
      sx={{
        width: fullWidth ? '100%' : 315,
        ...sx,
      }}
    >
      <CardActionArea sx={{ height: '100%', p: 5 }}>
        <CardHeader
          sx={{ p: 0, textDecoration: 'none', color: 'inherit' }}
          component={Link}
          href={generatePath(routerPages.UserProfile, {
            userId: c.commenter.id,
          })}
          avatar={<Avatar src={c.commenter.avatar?.smallUri || undefined} />}
          title={
            <Typography color="text.secondary" variant="subtitle2">
              {c.commenter.displayName}
            </Typography>
          }
          subheader={c.commenter.formattedAccountId}
          subheaderTypographyProps={{
            color: 'text.secondary',
            variant: 'overline',
          }}
        />
        <Link
          href={generatePath(routerPages.CommentDetail, { commentId: c.id })}
          sx={{ textDecoration: 'none', color: 'inherit' }}
        >
          <CardContent sx={{ pt: 4, pb: 5, px: 0 }}>
            <Typography variant="body2">{c.content}</Typography>
          </CardContent>
          <CardActions
            sx={{
              p: 0,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography color="text.secondary" variant="caption">
              {c.formattedCreatedAt}
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              spacing={5}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton.Like
                  onClick={e => {
                    e.stopPropagation()
                    e.preventDefault()
                    likeComment && likeComment()
                  }}
                  onMouseDown={e => {
                    //disable ripple on parent
                    e.stopPropagation()
                  }}
                  onTouchStart={e => {
                    e.stopPropagation()
                    e.preventDefault()
                  }}
                  disableTouchRipple={!likeComment}
                  liked={c.isLike}
                />
                <Typography variant="caption">{c.likeCount}</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Icons.Reply />
                <Typography variant="caption">{c.replyCount}</Typography>
              </Stack>
            </Stack>
          </CardActions>
        </Link>
      </CardActionArea>
    </Card>
  )
}
